import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { reportQueryFailure } from "@/lib/queryFailure";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { DeployRolloverOnNavigation } from "@/components/DeployUpdateNotice";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/hooks/useAuth";
import { ImpersonationProvider } from "@/contexts/ImpersonationContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import SuperAdmin from "./pages/SuperAdmin";
import TeamManagementPage from "./pages/TeamManagementPage";
import Invite from "./pages/Invite";
import NotFound from "./pages/NotFound";

// Global catch for EVERY failed query/mutation — see src/lib/queryFailure.ts.
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => reportQueryFailure(error, 'query', query.queryKey),
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => reportQueryFailure(error, 'rpc', mutation.options.mutationKey),
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,           // 30s — data is "fresh" for 30s after fetch
      refetchOnWindowFocus: true,  // re-fetch when user returns to tab / closes popup
      refetchOnMount: true,        // re-fetch when component mounts (e.g. navigating back)
      retry: 1,
    },
  },
});

const App = () => (
  <ThemeProvider defaultTheme="light" forcedTheme="light">
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <BrowserRouter>
          <ImpersonationProvider>
          <AuthProvider>
            <Routes>
              <Route path="/auth" element={<Auth />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/invite/:token" element={<Invite />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Index />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/super-admin"
                element={
                  <ProtectedRoute allowedRoles={['super_admin', 'platform_admin']}>
                    <SuperAdmin />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/team-management"
                element={
                  <ProtectedRoute allowedRoles={['super_admin', 'platform_admin', 'org_owner', 'org_admin']}>
                    <TeamManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<NotFound />} />
            </Routes>
            <DeployRolloverOnNavigation />
          </AuthProvider>
          </ImpersonationProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
